import {
  DocumentTemplate,
  ExamDemand,
  FolderItem,
  Patient,
  PatientDocument,
  Reference,
  ReferenceCategory,
} from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import { getUserId, jsonAbstract, uid } from "@/src/utils/utils";
import moment from "moment";
import { useState } from "react";
import { closeDrawer, openDrawer } from "../../components/dialogs";

import { Card, CheckboxCard, CompartmentCard } from "@/src/components/cards";

import { Button } from "@/src/components/buttons";
import {
  FetchableList,
  ListConstructor,
} from "@/src/components/fetchableLists";
import { Input } from "@/src/components/universalInput";
import { openEditor } from "../documents/documentsHook";

import { getStandardConfidentiality } from "../patients/confidentialitiesHook";
import { ExamForm } from "../exams/examForm";
import { getExamsDemandLongLetter } from "../documents/examDemandsLongLetter";

function ExamDemandsCompartment({
  patient,
  parent,
}: {
  patient: Patient;
  parent: FolderItem;
}) {
  let [searchKey, setSearchKey] = useState("");
  const [reload, setReload] = useState(0);

  return (
    <CompartmentCard
      title={"Examens demandés"}
      color="orange"
      secondaryButtonTitle={"Imprimer"}
      onSecondaryClick={async () => {
        let examDemands = await apiFetch("examDemands", "loadMany", {
          patientId: patient.id || "",
          parentId: parent.id,
        });
        if (!examDemands)
          throw new Error("Erreur lors du chargement des examens");
        await openExamDemandsEditor({
          examDemands,
          patient,
          parent,
        });
      }}
      onClick={async () => {
        await openDrawer({
          id: "examChoiceDialog",
          title: "Choisir un examen",
          content: (
            <ExamDemandsChoiceList
              categoriesIds={[
                "exam",
                "biology",
                "imaging",
                "exploration",
                "ecg",
              ]}
              onSelect={async (reference) => {
                let examDemand: ExamDemand = {
                  patient: patient,
                  creator: getUserId(),
                  readBy: [getUserId()],

                  parent,
                  type: "examDemand",
                  reference,
                  confidentiality: getStandardConfidentiality(patient),
                  content: {
                    reference,
                    fieldAnswers: [],
                    creator: getUserId(),
                    patient: patient,
                    
                  },
                };

                await apiFetch(
                  "examDemands",
                  "addOne",
                  {
                    examDemand,
                  },
                  "Erreur lors de la création de l'examen"
                );
                setReload(reload + 1);
                closeDrawer("examChoiceDialog");
              }}
            />
          ),
        });
      }}
    >
      <FetchableList
        queryKey={["ExamDemandsCompartment", searchKey, patient.id, reload]}
        queryFn={() =>
          apiFetch("examDemands", "loadMany", {
            patientId: patient.id || "",
            parentId: parent.id,
          })
        }
      >
        {(data) => (
          <div className="grid c1 gap10">
            {data?.map((examDemand) => {
              return (
                <Card
                  key={uid()}
                  color="orange"
                  title={examDemand?.reference?.name}
                >
                  <div className="gray">
                    {moment(examDemand?._date).format("LL HH:mm")}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </FetchableList>
    </CompartmentCard>
  );
}

function ExamDemandsChoiceList({
  categoriesIds,
  onSelect,
}: {
  categoriesIds?: ReferenceCategory[];
  onSelect: (newValue: Reference) => void;
}) {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState<string | null>();

  return (
    <div className="c1 gap10">
      <FetchableList
        queryKey={["examsList", searchKey, ...(categoriesIds || []), reload]}
        queryFn={() =>
          apiFetch(
            "references",
            "loadMany",
            {
              userId: getUserId(),
              searchKey,
              categoriesIds: categoriesIds,
            },
            "Erreur lors du chargement des examens"
          )
        }
        searchKey={searchKey}
        onSearch={setSearchKey}
        emptyChild={
          searchKey && (
            <div className="center grid c1 gap10 p20">
              Aucun examen disponible
              <Button
                onClick={async () => {
                  let reference = {
                    name: `${searchKey || ""}`,
                  } as Reference;
                  openDrawer({
                    title: "Ajouter un examen",
                    content: (
                      <ExamForm
                        current={reference}
                        onChange={async (newValue) => {
                          reference = newValue;
                        }}
                      />
                    ),
                    onConfirm: async () => {
                      await apiFetch(
                        "references",
                        "addOne",
                        { reference },
                        "Erreur lors de la création de l'examen"
                      );
                      setSearchKey("");
                      setReload((prev) => prev + 1);
                    },
                  });
                }}
              >
                Créer {searchKey}
              </Button>
            </div>
          )
        }
      >
        {(data) => (
          <div className="grid c1 gap10">
            {data?.map((exam) => {
              return (
                <Card
                  key={uid()}
                  color="orange"
                  onClick={async () => {
                    onSelect(exam);
                  }}
                >
                  <div>{exam.name}</div>
                </Card>
              );
            })}
          </div>
        )}
      </FetchableList>
    </div>
  );
}

async function openExamDemandsEditor({
  examDemands,
  patient,
  parent,
}: {
  examDemands: ExamDemand[];
  patient: Patient;
  parent: FolderItem;
}) {
  let selectedFormat: "short" | "letter" = "short";
  let selectedExamDemands = jsonAbstract(examDemands);
  await openDrawer({
    id: "printFormat",
    title: "Imprimer les examens",
    content: (
      <div className="grid c1 gap10">
        <Input
          type="chips"
          name={"Format"}
          value={selectedFormat}
          options={[
            {
              value: "short",
              label: "Court",
            },
            {
              value: "letter",
              label: "Lettre",
            },
          ]}
          onChange={(newValue) => {
            selectedFormat = newValue;
          }}
        ></Input>
        <ExamDemandsCheckboxList
          allDemands={examDemands}
          current={selectedExamDemands}
          onChange={(newValue) => {
            selectedExamDemands = newValue;
          }}
        ></ExamDemandsCheckboxList>
      </div>
    ),
    onConfirm: async () => {
      let template: DocumentTemplate = {
        title: "Demande d'examens",
        content:
          selectedFormat == "short"
            ? await getExamsDemandShortLetter({
                examDemands: selectedExamDemands,
                patient,
              })
            : await getExamsDemandLongLetter({
                examDemands: selectedExamDemands,
                patient,
              }),
        barcodeLocation: "none",
        creator: getUserId(),
        format: "a4",
        isValidatable: false,
        name: "",
        parent: null,
        showHeader: true,
        showLeftBar: false,
        showPatientIdentity: true,
        showTitle: true,
        type: "letter",
      };

      let patientDocument: PatientDocument = {
        content: template.content,
        title: template.title,
        creator: getUserId(),
        patient,
        refAnswer: null,
        template,
        status: "pending",
        parent,
      };
      await openEditor({
        patient,
        patientDocument,
      });
    },
  });
}

export function ExamDemandsCheckboxList({
  allDemands,
  current,
  onChange,
}: {
  allDemands: ExamDemand[];
  current: ExamDemand[];
  onChange: (newValue: ExamDemand[]) => void;
}) {
  const [examDemands, setExamDemands] = useState(current);

  function isSelected(demand: ExamDemand) {
    return examDemands.find((u) => u?.id == demand?.id) != null;
  }

  return (
    <div className="c1 gap15">
      <ListConstructor
        data={examDemands}
        isLoading={false}
        isError={false}
        error={null}
      >
        <div className={`usersList grid c1 mc1 gap10`}>
          {allDemands?.map((demand) => {
            if (!demand) return null;
            return (
              <CheckboxCard
                key={uid()}
                color="orange"
                title={demand.reference?.name}
                status={isSelected(demand) ? "active" : "inactive"}
                onClick={async () => {
                  let newValue = examDemands;
                  if (isSelected(demand)) {
                    newValue = examDemands.filter((u) => u?.id != demand?.id);
                  } else {
                    newValue = [...examDemands, demand];
                  }
                  setExamDemands([...newValue]);
                  onChange([...newValue]);
                }}
              ></CheckboxCard>
            );
          })}
        </div>
      </ListConstructor>
    </div>
  );
}

export { ExamDemandsCompartment };
  function getExamsDemandShortLetter(arg0: { examDemands: any; patient: Patient; }): string | PromiseLike<string> {
    throw new Error("Function not implemented.");
  }

